import {
    Dialog,
    Flex,
    Title,
    Button,
    Separator,
    PriorityIcon,
    Link,
    Icon
} from '@priority-software/priority-style-react';
import React, {useEffect, useRef, useState} from 'react';
import styles from '../styles/formDesignPanel.module.scss';
import {DesignPanels, getDesignTypeByActivePanel} from '../enums';
import cx from 'classnames';
import {FieldsTableDesignContent} from './FormDesignContent';
import {apiLocker, formsStore} from '../../../stores/Form';
import {formsStore as legacyStore} from '../../../_legacy/Form/stores/FormsStore';
import {Form, FormViewEnum} from '../../../stores/Form/Form';
import {
    fetchActionsDesign,
    fetchRowViewColumnsDesign,
    fetchSubformsDesign,
    fetchTableViewColumnsDesign,
    packageSet,
    restoreDesign,
    saveActionsDesign,
    saveRowViewColumnsDesign,
    saveSubformsDesign,
    saveTableViewColumnsDesign,
    systemPackages
} from '../service';
import text, {getServerTexts} from '../texts';
import {priority} from '@priority-software/priority-proto-api/lib';
import {openPackagesPanel, setIsNavigationEnabled} from '../../../_shared/GWTBridge/invoke/genPage';
import {getCurrentForm, getMainCurrentForm, getReloadModeByPanelType} from '../utils/crossAPI';
import {Form as LegacyForm} from '../../../_legacy/Form/stores/Form';
import {getDesignIndex} from '../utils/tab';
import {showConfirmMessage} from '../../../_shared/Messages/actions/MessageActions';
import texts from '../texts';
import {reloadLegacyForm} from '../../../_legacy/Form/services/formService';
import {
    refreshSidePanels,
    setNetformIsDesigning,
    triggerHeaderRerender
} from '../../../_shared/GWTBridge/invoke/netForm';
import {maxFreezeIndex, numFieldsPerTab} from '../consts';
import {useEventListener} from '../../../hooks/useEventListener';
import {keys} from '../../../constants/KeyCodes';
import {getActionsPackMapping, getSubformsPackMapping} from '../legacyService';
import {ScreenLocker} from '../../Form/components/ScreenLocker';
import {getTopDialogZindex} from '../../../_shared/GWTBridge/invoke/priModalDlg';

type FormDesignPanelProps = {
    onClose: () => void;
};

export const FormDesignPanel = ({onClose}: FormDesignPanelProps) => {
    const [activePanel, setActivePanel] = useState(DesignPanels.FIELDS);
    const [textsReady, setTextsReady] = useState(false);
    const [data, setData] = useState([]);
    const [pic, setPic] = useState(priority.netitems.form.Picture.NoPicture);
    const [isLoading, setIsLoading] = useState(false);
    const [packages, setPackages] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const isDirty = useRef(false);
    const isPackagesDirty = useRef(false);

    useEffect(() => {
        const fetchTexts = async () => {
            await getServerTexts();
            setTextsReady(true);
        };
        const fetchPackages = async () => {
            const res = await systemPackages();
            const packages = res.PackagesByCategory.flatMap(category => category.Packages);
            setPackages(packages);
        };
        const session = formsStore.getActiveForm()?.session;
        session
            ? apiLocker.lockSession(session, {navigationOnly: true})
            : setIsNavigationEnabled(false);
        fetchTexts();
        fetchPackages();
        return () => (session ? apiLocker.unlockSession(session) : setIsNavigationEnabled(true));
    }, []);

    useEffect(() => {
        const fetchDesign = async () => {
            const curForm: Form | LegacyForm = getCurrentForm();
            setIsLoading(true);
            const _designRes = getTabData(activePanel);
            let _packMappingRes;
            if (legacyStore.currentSession) {
                const formInnerId = legacyStore.currentForm.formJson.form;
                if (activePanel === DesignPanels.ACTIONS) {
                    _packMappingRes = getActionsPackMapping(formInnerId);
                }
                if (activePanel === DesignPanels.SUBFORMS) {
                    _packMappingRes = getSubformsPackMapping(formInnerId);
                }
            }
            const designRes = await _designRes;
            const packMappingRes = await _packMappingRes;
            const design = getDesign(activePanel, designRes, packMappingRes);
            setPic(
                activePanel === DesignPanels.FIELDS && curForm.view === FormViewEnum.form
                    ? (designRes as priority.netitems.form.RowViewColumnsDesignRs).Picture
                    : priority.netitems.form.Picture.NoPicture
            );
            isDirty.current = false;
            isPackagesDirty.current = false;
            setData(design);
            setIsLoading(false);
        };
        fetchDesign();
    }, [activePanel]);

    const updateActivePanel = async (panel: DesignPanels) => {
        if (isLoading) {
            return;
        }
        if (isDirty.current) {
            showConfirmMessage({
                title: texts.beforeYouLeave,
                content: texts.anotherTabWarn,
                confirmButtonText: texts.yes,
                cancleButtonText: texts.no,
                defaultButton: 0,
                messageIcon: PriorityIcon.attention,
                onConfirm: async () => {
                    confirmTabChange(panel);
                },
                onCancel: () => {
                    finishUpdateActivePanel(panel);
                }
            });
        } else {
            finishUpdateActivePanel(panel);
        }
    };

    const confirmTabChange = async (panel: DesignPanels) => {
        await saveTabData();
        finishUpdateActivePanel(panel);
    };

    const saveTabData = async () => {
        setIsSaving(true);
        const curForm: Form | LegacyForm = getCurrentForm();
        switch (activePanel) {
            case DesignPanels.FIELDS:
                if (curForm.view === FormViewEnum.table) {
                    await saveTableViewColumnsDesign(
                        data,
                        curForm.ename,
                        getMainCurrentForm()?.ename
                    );
                } else {
                    await saveRowViewColumnsDesign(
                        data,
                        curForm.ename,
                        getMainCurrentForm()?.ename,
                        pic
                    );
                }
                break;
            case DesignPanels.ACTIONS:
                await saveActionsDesign(data, curForm.ename, getMainCurrentForm()?.ename);
                break;
            case DesignPanels.SUBFORMS:
            case DesignPanels.PANELS:
                await saveSubformsDesign(data, curForm.ename, getMainCurrentForm()?.ename);
                break;
            default:
                break;
        }
        if (isPackagesDirty.current) {
            await packageSet(packages.filter(p => p.isDirty));
        }
        refreshForm();
        setIsSaving(false);
    };

    const refreshForm = async () => {
        const curForm = getCurrentForm();
        if (legacyStore.currentSession) {
            setNetformIsDesigning(true);
            await reloadLegacyForm(getReloadModeByPanelType(activePanel, curForm.view));
            if (activePanel === DesignPanels.ACTIONS) {
                triggerHeaderRerender();
            }
            if (activePanel === DesignPanels.PANELS || activePanel === DesignPanels.SUBFORMS) {
                refreshSidePanels();
            }
        }
    };

    const finishUpdateActivePanel = (panel: DesignPanels) => {
        if (panel !== activePanel) {
            setIsLoading(true);
            setData([]);
            setActivePanel(panel);
        }
    };

    const updatePinPosition = newData => {
        const visibleFields = newData.filter(i => !i.Hidden);
        const coverItemVisibleIndex = visibleFields.findIndex(i => i.Cover);
        if (coverItemVisibleIndex > maxFreezeIndex) {
            visibleFields[coverItemVisibleIndex].Cover = false;
            visibleFields[maxFreezeIndex].Cover = true;
        }
        if (coverItemVisibleIndex === -1) {
            const coverItem = newData.find(i => i.Cover);
            coverItem.Cover = false;
            visibleFields[0].Cover = true;
        }
    };

    const updateData = newData => {
        isDirty.current = true;
        if (activePanel === DesignPanels.FIELDS && getCurrentForm().view === FormViewEnum.table) {
            updatePinPosition(newData);
        }
        setData(newData);
    };

    const handleClose = callback => {
        if (isDirty.current) {
            showConfirmMessage({
                title: texts.beforeYouLeave,
                content: texts.exitWithoutSaving,
                confirmButtonText: texts.yes,
                cancleButtonText: texts.cancel,
                defaultButton: 0,
                messageIcon: PriorityIcon.attention,
                onConfirm: () => {
                    onClose();
                    callback?.();
                }
            });
        } else {
            onClose();
            callback?.();
        }
    };
    useEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === keys.ESC) {
            onDialogClose();
        }
    });

    const onDialogClose = () => {
        handleClose(null);
    };

    const setChangedPakages = (packages: priority.netitems.ngen.Package[]) => {
        isPackagesDirty.current = true;
        setPackages(packages);
    };

    return (
        <>
            <Dialog
                componentClasses={styles.panel}
                isOpen={textsReady}
                zIndex={getTopDialogZindex()}
                onClose={onDialogClose}
                keyboard={false}
                title={<DesignTitle handleClose={handleClose} />}
                content={
                    <DesignContent
                        data={data}
                        pic={pic}
                        setPic={setPic}
                        updateData={updateData}
                        activePanel={activePanel}
                        setActivePanel={updateActivePanel}
                        isLoading={isLoading}
                        packages={packages}
                        setPackages={setChangedPakages}
                    />
                }
                footer={
                    <Footer
                        activePanel={activePanel}
                        data={data}
                        onClose={onClose}
                        onCancel={handleClose}
                        saveTabData={saveTabData}
                        refreshForm={refreshForm}
                        isSaving={isSaving}
                    />
                }
            />
            <ScreenLocker showLoader={isSaving} locked={isSaving} />
        </>
    );
};

const DesignTitle = ({handleClose}) => {
    const handlePackagesPanelOpen = () => {
        handleClose(openPackagesPanel);
    };
    return (
        <Flex direction="column">
            <Flex justifyContent="flex-end" componentClasses={styles.packagesCenter}>
                <Icon icon={PriorityIcon.deployedCode}></Icon>
                <Link skin="secondary" onClick={handlePackagesPanelOpen}>
                    {texts.packagesDesign}
                </Link>
            </Flex>
            <Flex justifyContent="flex-start" componentClasses={styles.titleContainer}>
                <Title componentClasses={styles.titleText} skin="h3" textColor="primary-700">
                    {text.formDesignHeader}
                </Title>
                <Title componentClasses={styles.titleText} skin="h3" textColor="primary-500">
                    {getCurrentForm().title}
                </Title>
            </Flex>
        </Flex>
    );
};

/*
 ** TODO:
 ** 1. Use localized texts
 ** 2. Check if the tab is really needed (there're subfoms to display in "Panels" tab, for example)
 */

type Tab = {
    key: DesignPanels;
    title: string;
};

const getTabs = (): Tab[] => {
    const tabs = [
        {key: DesignPanels.FIELDS, title: text.fields},
        {key: DesignPanels.ACTIONS, title: text.actions},
        {key: DesignPanels.SUBFORMS, title: text.subforms}
    ];
    !legacyStore.currentSession && tabs.push({key: DesignPanels.PANELS, title: text.panels});
    return tabs;
};

// TODO: update return type when all the data comes from the server

const getTabData = async (activePanel: DesignPanels) => {
    const curForm: Form | LegacyForm = getCurrentForm();
    switch (activePanel) {
        case DesignPanels.FIELDS:
            if (curForm.view === FormViewEnum.table) {
                return fetchTableViewColumnsDesign(curForm.ename, getMainCurrentForm()?.ename);
            } else {
                return fetchRowViewColumnsDesign(curForm.ename, getMainCurrentForm()?.ename);
            }
        case DesignPanels.ACTIONS:
            return fetchActionsDesign(curForm.ename, getMainCurrentForm()?.ename);
        case DesignPanels.SUBFORMS:
            return fetchSubformsDesign(curForm.ename, getMainCurrentForm()?.ename);
        case DesignPanels.PANELS:
            return fetchSubformsDesign(curForm.ename, getMainCurrentForm()?.ename);
        default:
            return [];
    }
};

const isSidePanelSubform = (name: string): boolean => {
    const sidePanelSubforms = Object.values(formsStore.sidePanelSubforms);
    return sidePanelSubforms.includes(name);
};

const updateDesignWithPackageId = (design, mapping) => {
    if (!mapping || !mapping.length) return design;

    return design.map(designItem => {
        const mappingItem = mapping.find(m => m.execrun === designItem.Exec.toString());
        designItem.PackageId = mappingItem?.pack ? Number(mappingItem?.pack) : null;
        return designItem;
    });
};

const prepareTabsDesign = (res: priority.netitems.form.RowViewColumnsDesignRs) => {
    const tabDesign = [...res?.TabsDesign];
    tabDesign.forEach((tab, tabIndex) => {
        tab.ColumnsDesign.forEach((column, index) => {
            if (index === 0) {
                column.Newarea = false;
            }
            if (tabIndex === 0) {
                const designIndex: number = getDesignIndex(tab.ColumnsDesign, index);
                if (designIndex === numFieldsPerTab + 1) {
                    column.Newarea = false;
                }
            }
        });
    });
    return tabDesign;
};

const getDesign = (activePanel: DesignPanels, res: any, packMappingRes: any) => {
    const curForm: Form | LegacyForm = getCurrentForm();
    switch (activePanel) {
        case DesignPanels.FIELDS:
            if (curForm.view === FormViewEnum.table) {
                return res?.ColumnsDesign;
            } else {
                return prepareTabsDesign(res);
            }
        case DesignPanels.ACTIONS:
            if (!res?.ActionsDesign.length) return [];
            return legacyStore.currentSession
                ? updateDesignWithPackageId(
                      res?.ActionsDesign,
                      packMappingRes.General?.[0].FormPackActivations?.[0].activation
                  )
                : res.ActionsDesign;
        case DesignPanels.SUBFORMS:
            if (!res?.SubformsDesign.length) return [];
            return legacyStore.currentSession
                ? updateDesignWithPackageId(
                      res?.SubformsDesign,
                      packMappingRes.General?.[0].FormPackSubforms?.[0].subform
                  )
                : res?.SubformsDesign.filter(subform => !isSidePanelSubform(subform.Name));
        case DesignPanels.PANELS:
            return res?.SubformsDesign.filter(subform => isSidePanelSubform(subform.Name));
        default:
            return [];
    }
};

const DesignContent = ({
    data,
    updateData,
    pic,
    setPic,
    activePanel,
    setActivePanel,
    isLoading,
    packages,
    setPackages
}) => {
    const tabs = getTabs();
    return (
        <Flex
            direction="column"
            justifyContent="flex-start"
            componentClasses={styles.contentContainer}
        >
            <Flex justifyContent="flex-start" componentClasses={styles.tabs}>
                {tabs.map(tab => {
                    return (
                        <TabButton
                            key={tab.key}
                            title={tab.title}
                            onClick={() => setActivePanel(tab.key)}
                            isActive={activePanel === tab.key}
                        />
                    );
                })}
            </Flex>
            <Separator align="vertical" componentClasses={styles.separator} />
            <FieldsTableDesignContent
                key={activePanel}
                activePanel={activePanel}
                data={[...data]}
                updateData={updateData}
                pic={pic}
                setPic={setPic}
                isLoading={isLoading}
                packages={packages}
                setPackages={setPackages}
            />
        </Flex>
    );
};

type TabButtonProps = {
    title: string;
    onClick: () => void;
    isActive: boolean;
};

const TabButton = ({title, onClick, isActive}: TabButtonProps) => {
    return (
        <Button
            componentClasses={cx(styles.tabButton, {[styles.tabButtonActive]: isActive})}
            onClick={onClick}
        >
            {title}
        </Button>
    );
};

const Footer = ({activePanel, data, onClose, saveTabData, refreshForm, onCancel, isSaving}) => {
    const onSave = async () => {
        await saveTabData();
        onClose();
    };

    const restoreDefaults = async () => {
        const curForm: Form | LegacyForm = getCurrentForm();
        await restoreDesign(
            curForm.ename,
            getMainCurrentForm()?.ename,
            getDesignTypeByActivePanel(activePanel, curForm.view)
        );
        await refreshForm();
        onClose();
    };

    const handleRestoreDefaults = () => {
        showConfirmMessage({
            defaultButton: 0,
            content: texts.instructions,
            confirmButtonText: text.yes,
            cancleButtonText: text.no,
            onCancel: () => {},
            onConfirm: restoreDefaults
        });
    };

    return (
        <Flex justifyContent="space-between" componentClasses={styles.footer}>
            {activePanel !== DesignPanels.PANELS && (
                <Button
                    skin="text"
                    prefixIcon={PriorityIcon.refresh}
                    onClick={handleRestoreDefaults}
                    disabled={isSaving}
                >
                    {text.restoreDefaults}
                </Button>
            )}
            <Flex justifyContent="flex-end">
                <Button
                    skin="secondary"
                    componentClasses={styles.footerButton}
                    onClick={() => onCancel(null)}
                    disabled={isSaving}
                >
                    {text.cancel}
                </Button>
                <Button
                    componentClasses={styles.footerButton}
                    onClick={onSave}
                    disabled={!data || !data.length || isSaving}
                >
                    {text.save}
                </Button>
            </Flex>
        </Flex>
    );
};
